<script setup lang="ts">
import { ref } from 'vue';
import type { LockedResult } from '../composables/useScanner';

const props = defineProps<{
  result: LockedResult;
  lookupStatus: 'idle' | 'loading' | 'error';
  lookupError: string | null;
}>();
const emit = defineEmits<{
  copy: [text: string];
  favorite: [];
  edit: [text: string];
  lookup: [text: string];
  rescan: [];
}>();

const editing = ref(false);
const text = ref(props.result.text);
const copied = ref(false);

async function onCopy(): Promise<void> {
  emit('copy', text.value);
  copied.value = true;
  setTimeout(() => (copied.value = false), 1200);
}

function onEditToggle(): void {
  if (editing.value) {
    const next = text.value.toUpperCase();
    text.value = next;
    emit('edit', next);
    emit('lookup', next);
  }
  editing.value = !editing.value;
}
</script>

<template>
  <div class="result-card">
    <div class="plate-row">
      <input
        v-if="editing"
        v-model="text"
        class="plate-edit"
        autocapitalize="characters"
        spellcheck="false"
        @keyup.enter="onEditToggle"
      />
      <div v-else class="plate-text">{{ text }}</div>
    </div>
    <div class="meta-row">
      <span v-if="result.region" class="chip">{{ result.region }}</span>
      <span class="chip dim">{{ Math.round(result.confidence * 100) }}%</span>
    </div>
    <div v-if="lookupStatus !== 'idle'" class="lookup-state" :class="lookupStatus">
      <span v-if="lookupStatus === 'loading'" class="spinner" aria-hidden="true"></span>
      <span>
        {{ lookupStatus === 'loading' ? $t('lookup.checking') : lookupError }}
      </span>
    </div>
    <div class="action-row">
      <button class="btn" @click="onCopy">{{ copied ? $t('result.copied') : $t('result.copy') }}</button>
      <button
        class="btn star-only"
        :class="{ fav: result.favorite }"
        :aria-label="$t('aria.favorite')"
        @click="$emit('favorite')"
      >
        {{ result.favorite ? '★' : '☆' }}
      </button>
      <button class="btn" @click="onEditToggle">
        {{ editing ? $t('result.done') : $t('result.edit') }}
      </button>
      <button
        v-if="lookupStatus === 'error'"
        class="btn primary"
        @click="$emit('lookup', text)"
      >
        {{ $t('lookup.retry') }}
      </button>
      <button v-else class="btn primary" @click="$emit('rescan')">{{ $t('result.rescan') }}</button>
    </div>
  </div>
</template>
